import React, { useEffect, useState } from 'react';
import { Plane, Car, Calendar, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { weddingData } from '../mockData';

const Transport = () => {
  const { language } = useLanguage();
  const content = weddingData[language].transport;
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const getIcon = (iconName) => {
    const iconProps = { size: 32, className: "text-lilac-600" };
    switch (iconName) {
      case 'plane': return <Plane {...iconProps} />;
      case 'car': return <Car {...iconProps} />;
      case 'calendar': return <Calendar {...iconProps} />;
      default: return <Car {...iconProps} />;
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-lilac-600 via-lilac-500 to-lilac-700 py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-gold-400 rounded-full filter blur-3xl animate-float" />
          <div className="absolute bottom-20 right-20 w-64 h-64 bg-lilac-300 rounded-full filter blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        </div>

        <div className={`container mx-auto px-6 sm:px-8 lg:px-12 text-center relative z-10 transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <Car className="mx-auto text-gold-400 mb-8" size={64} />
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-playfair font-bold text-white mb-6">
            {content.title}
          </h1>
          <p className="text-xl text-white/90 font-montserrat max-w-3xl mx-auto">
            {content.subtitle}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-gold-300 to-transparent mx-auto rounded-full mt-8" />
        </div>
      </section>

      {/* Transport Sections */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto space-y-12">
            {content.sections.map((section, index) => (
              <div
                key={index}
                className={`card-premium transition-all duration-700 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {/* Section Header */}
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-lilac-100 to-gold-50 rounded-2xl flex items-center justify-center shadow-md">
                    {getIcon(section.icon)}
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-playfair font-bold text-lilac-700">
                    {section.title}
                  </h2>
                </div>

                {/* Content */}
                <p className="text-gray-700 font-montserrat leading-relaxed mb-6">
                  {section.content}
                </p>

                {/* Tips (if available) */}
                {section.tips && (
                  <div className="bg-gradient-to-br from-lilac-50 to-gold-50 rounded-xl p-6 mb-4">
                    <ul className="space-y-3">
                      {section.tips.map((tip, tipIndex) => (
                        <li key={tipIndex} className="flex items-start space-x-3">
                          <span className="text-gold-500 font-bold">✓</span>
                          <p className="text-gray-700 font-montserrat">{tip}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Options (if available) */}
                {section.options && (
                  <div className="grid sm:grid-cols-3 gap-4 mb-4">
                    {section.options.map((option, optIndex) => (
                      <div key={optIndex} className="bg-gradient-to-br from-lilac-50 to-white rounded-xl p-4 border border-lilac-100">
                        <h4 className="font-playfair font-bold text-lilac-700 mb-2">
                          {option.name}
                        </h4>
                        <p className="text-gray-600 font-montserrat text-sm">
                          {option.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Note (if available) */}
                {section.note && (
                  <div className="flex items-start space-x-3 bg-gold-50 border-l-4 border-gold-500 rounded-r-xl p-4">
                    <AlertCircle className="text-gold-600 flex-shrink-0 mt-0.5" size={20} />
                    <p className="text-gray-700 font-montserrat text-sm italic">
                      {section.note}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Transport;
